import React, { useEffect } from 'react'
import { Button, Result, Table } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useFetchQuestionsQuery } from '../../../../../store/apis/question.api'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { questionActions } from '../../../../../store/slices/question.slice'
import {
    getQuestionProcessingStatusName,
    QuestionProcessingStatuses,
} from '../../../../../constants/question-processing-statuses'
import { useVersionId } from '../../../../../utils/parseURL.helper'
import { isNumber, truncate } from 'lodash'
import { useFetchQuestionsApproveAnalyticsQuery } from '../../../../../store/apis/analytics.api'
import QuestionDefinitionActions from '../question-definition/QuestionDefinitionActions'

const useAutoExpandParentRows = (dataItems) => {
    const [expandedRowKeys, setExpandedRowKeys] = React.useState([])

    useEffect(() => {
        if (dataItems) {
            const parentKeys = dataItems
                .filter(item => item.children?.length > 0)
                .map(item => item.id)
            setExpandedRowKeys(parentKeys)
        }
    }, [dataItems])

    return {
        expandedRowKeys,
        onExpandedRowsChange: expandedKeys => setExpandedRowKeys(expandedKeys)
    }
}

const QuestionsTable = () => {
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()

    const { data: analyticsPerQuestion } = useFetchQuestionsApproveAnalyticsQuery({ token })
    const { data: questions, isFetching, error, refetch } = useFetchQuestionsQuery({ token, versionId })
    
    const { expandedRowKeys, onExpandedRowsChange } = useAutoExpandParentRows(questions)

    const getQuestionStatus = question => {
        if (question.processing.status !== QuestionProcessingStatuses.READY)
            return getQuestionProcessingStatusName(question.processing.status)
        else if (question.lastTrained) return 'Trained'
        else return 'Untrained'
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, question) => (
                <a type='link' onClick={() => dispatch(questionActions.setEditingQuestionId(question.key))}>
                    {truncate(name, { length: 96 })}
                </a>
            ),
        },
        {
            title: 'Categories',
            dataIndex: 'categories',
            key: 'categories',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Review statistics',
            children: [
                {
                    title: 'Unreviewed',
                    dataIndex: 'unreviewed',
                    key: 'unreviewed',
                    render: (unreviewed, record) =>
                        isNumber(unreviewed)
                            ? `${unreviewed}% (${Math.round((record.totalSubmissions * unreviewed) / 100)} submissions)`
                            : null,
                },
                {
                    title: 'AI reviewed',
                    dataIndex: 'aiReviewed',
                    key: 'aiReviewed',
                    render: (aiReviewed, record) =>
                        isNumber(aiReviewed)
                            ? `${aiReviewed}% (${Math.round((record.totalSubmissions * aiReviewed) / 100)} submissions)`
                            : null,
                },
                {
                    title: 'Human reviewed',
                    dataIndex: 'humanReviewed',
                    key: 'humanReviewed',
                    render: (humanReviewed, record) =>
                        isNumber(humanReviewed)
                            ? `${humanReviewed}% (${Math.round(
                                  (record.totalSubmissions * humanReviewed) / 100,
                              )} submissions)`
                            : null,
                },
            ],
        },
        {
            title: 'Action',
            key: 'action',
            width: '8%',
            render: question => (
                <div className='d-flex'>
                    <QuestionDefinitionActions question={question} />
                    <Button
                        shape='circle'
                        type='text'
                        aria-label='Edit'
                        title='Edit'
                        onClick={() => dispatch(questionActions.setEditingQuestionId(question.key))}
                    >
                        <EditOutlined className='primary-color' />
                    </Button>
                </div>
            ),
        },
    ]

    const data = questions?.map(question => {
        const children = question.children?.map(child => ({
            key: child.id,
            name: child.name || child.content,
            categories: child.categories.length,
            aiReviewed: analyticsPerQuestion?.[child.id]?.reviewedByAi,
            humanReviewed: analyticsPerQuestion?.[child.id]?.reviewedByHuman,
            unreviewed: analyticsPerQuestion?.[child.id]?.unreviewed,
            totalSubmissions: analyticsPerQuestion?.[child.id]?.totalSubmissions,
            status: getQuestionStatus(child),
            isChild: true,
        }))

        return {
            key: question.id,
            name: question.name || question.content,
            categories: children ? null : question.categories.length,
            aiReviewed: children ? null : analyticsPerQuestion?.[question.id]?.reviewedByAi,
            humanReviewed: children ? null : analyticsPerQuestion?.[question.id]?.reviewedByHuman,
            unreviewed: children ? null : analyticsPerQuestion?.[question.id]?.unreviewed,
            totalSubmissions: children ? null : analyticsPerQuestion?.[question.id]?.totalSubmissions,
            status: children ? null : getQuestionStatus(question),
            children,
        }
    })

    return error ? (
        <Result
            status='error'
            title='Questions fetch failed'
            subTitle={error}
            extra={[
                <Button key='try' onClick={() => refetch()} loading={isFetching}>
                    Try Again
                </Button>,
            ]}
        />
    ) : (
        <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            expandable={{
                rowExpandable: record => record.children?.length > 0,
            }}
            rowClassName={record => (record.isChild ? 'child-row' : 'parent-row')}
            loading={{ spinning: isFetching, tip: 'Loading questions...' }}
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={onExpandedRowsChange}
        />
    )
}

export default QuestionsTable
